import {
  CapaActionStatus,
  NONCONFORMITY_SOURCE_LABEL_PT_BR,
  NONCONFORMITY_STATUS_LABEL_PT_BR,
  NONCONFORMITY_TYPE_LABEL_PT_BR,
  NonconformityStatus,
  Permission,
  type GutScoreInput,
  type SignableAct,
  type SignedCommand,
} from '@qualicloud/contracts';
import { CRITICALITY_LABEL_PT_BR } from '@qualicloud/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { SignatureDialog } from '@/components/signature/signature-dialog';
import { PrintableRecord, SignatureManifest } from '@/components/signature/signature-manifest';
import { GutMatrix } from '@/components/sgq/gut-matrix';
import { IshikawaBoard, type IshikawaGroup } from '@/components/sgq/ishikawa-board';
import { NcClosureDialog } from '@/components/sgq/nc-closure-dialog';
import { NonconformityPicker } from '@/components/sgq/nonconformity-picker';
import {
  CapaActionStatusBadge,
  CapaDueBadge,
  CriticalityBadge,
  NonconformityStatusBadge,
} from '@/components/sgq/sgq-badges';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  FormError,
  IconAlertOctagon,
  IconAlertTriangle,
  IconCalendar,
  IconCheckCircle,
  IconClipboardCheck,
  IconClock,
  IconFile,
  IconHistory,
  IconInfo,
  IconUsers,
  LoadingRegion,
  RadioGroup,
  Skeleton,
  Table,
  Tabs,
  Textarea,
  cn,
  focusRing,
  formatBytes,
  useToast,
  type BadgeTone,
  type TableColumn,
} from '@/design-system';
import {
  capaApi,
  nonconformitiesApi,
  queryKeys,
  saveBlob,
  sgqSignedActInvalidations,
  toUserMessage,
} from '@/lib/api';
import type {
  CapaActionView,
  NonconformityDetail,
  NonconformityEvidence,
} from '@/lib/api/sgq.types';
import { formatDateOnly, formatRegulatedDateTime, truncateHash } from '@/lib/format/datetime';
import { signedActRecordId } from '@/lib/signed-act-record';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DETALHE DA NÃO-CONFORMIDADE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O CICLO INTEIRO NUMA TELA — e é a diferença central para o legado.
 *
 * No sistema antigo a NC nascia e MORRIA na avaliação do gestor:
 * `managerEvaluation` gravava `procede = 'S'` incondicionalmente e
 * `status_nc = 'F'` no MESMO UPDATE (`model/qualidade/qualidade.php:32-37`).
 * A regra que exigia plano de ação completo antes de encerrar existia — em
 * `finalizarNc`, marcada como C16 — mas era um ENDPOINT ÓRFÃO: nenhum template
 * do sistema o chamava (BUG-NC-04). Na prática, toda NC era encerrada sem
 * nenhuma ação corretiva, e ninguém via o problema.
 *
 * Aqui "procedente" leva a ACCEPTED, que é o COMEÇO do tratamento. O
 * encerramento é ato assinado à parte e o servidor recusa enquanto houver ação
 * pendente ou eficácia reprovada — e a tela DIZ QUAIS, com link, em vez de
 * mostrar um botão que falha.
 *
 * As abas cobrem o ciclo: Dados, Avaliação, Análise de causa (Ishikawa), Plano
 * de ação, Reuniões vinculadas, Reincidência, Evidências e Trilha.
 */

type PendingAct =
  | { readonly kind: 'evaluate'; readonly proceeds: boolean; readonly gut?: GutScoreInput }
  | { readonly kind: 'close'; readonly closingStatement: string };

export function NonconformityDetailPage() {
  const { nonconformityId } = useParams({ strict: false }) as { nonconformityId: string };
  const [tab, setTab] = useState('dados');
  const [pendingAct, setPendingAct] = useState<PendingAct | null>(null);
  const [evaluationOpen, setEvaluationOpen] = useState(false);
  const [closureOpen, setClosureOpen] = useState(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { can } = useAuth();
  const timeZone = useOrganizationTimeZone();

  const detailQuery = useQuery({
    queryKey: queryKeys.nonconformities.detail(nonconformityId),
    queryFn: () => nonconformitiesApi.detail(nonconformityId),
  });

  const nc = detailQuery.data;

  const capaQuery = useQuery({
    queryKey: queryKeys.capa.case(nc?.capaCaseId ?? ''),
    queryFn: () => capaApi.case(nc?.capaCaseId as string),
    enabled: Boolean(nc?.capaCaseId),
  });

  const invalidate = async () => {
    await Promise.all(
      sgqSignedActInvalidations().map((key) => queryClient.invalidateQueries({ queryKey: key })),
    );
    await queryClient.invalidateQueries({
      queryKey: queryKeys.nonconformities.detail(nonconformityId),
    });
  };

  const evaluate = useMutation({
    mutationFn: (params: { readonly command: SignedCommand; readonly act: PendingAct }) => {
      if (params.act.kind !== 'evaluate') throw new Error('Ato inválido para avaliação.');
      return nonconformitiesApi.evaluate(nonconformityId, {
        ...params.command,
        proceeds: params.act.proceeds,
        ...(params.act.gut ? { gut: params.act.gut } : {}),
        // A justificativa do ato É a justificativa da avaliação: o catálogo
        // marca NC_EVALUATE e NC_NOT_APPLICABLE com `reasonRequired`, e o
        // diálogo de assinatura já a exige. Pedir o mesmo texto duas vezes
        // faria o usuário escrever "conforme acima" no segundo campo.
        justification: params.command.reason ?? '',
        ...(nc?.recordHash ? { expectedRecordHash: nc.recordHash } : {}),
      });
    },
  });

  const close = useMutation({
    mutationFn: (params: { readonly command: SignedCommand; readonly act: PendingAct }) => {
      if (params.act.kind !== 'close') throw new Error('Ato inválido para encerramento.');
      return nonconformitiesApi.close(nonconformityId, {
        ...params.command,
        closingStatement: params.act.closingStatement,
        ...(nc?.recordHash ? { expectedRecordHash: nc.recordHash } : {}),
      });
    },
  });

  /**
   * Exportação em PDF. Não é `window.print()`: o registro precisa sair com o
   * manifesto de assinaturas e com evento na trilha, e nenhum dos dois existe
   * quando quem imprime é o navegador.
   */
  const exportPdf = useMutation({
    mutationFn: async () => {
      const file = await nonconformitiesApi.exportPdf(nonconformityId);
      saveBlob(file.blob, file.fileName);
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível exportar a não-conformidade',
        description: toUserMessage(error),
      }),
  });

  if (detailQuery.isPending) {
    return (
      <LoadingRegion loading label="Carregando a não-conformidade…">
        <div className="p-5">
          <Skeleton className="h-8 w-96" />
          <Skeleton className="mt-3 h-4 w-[28rem]" />
          <Skeleton className="mt-6 h-80 w-full" />
        </div>
      </LoadingRegion>
    );
  }

  if (!nc) {
    return (
      <PageBody>
        <Card>
          <EmptyState
            icon={<IconClipboardCheck size={22} />}
            title="Não-conformidade não encontrada"
            description="O registro não existe ou você não tem permissão para vê-lo. Se acha que deveria ter acesso, fale com o gestor da qualidade."
            action={
              <Button asChild variant="primary">
                <Link to="/nao-conformidades">Voltar à lista</Link>
              </Button>
            }
          />
        </Card>
      </PageBody>
    );
  }

  const awaitingEvaluation =
    nc.status === NonconformityStatus.OPEN || nc.status === NonconformityStatus.UNDER_EVALUATION;
  const canEvaluate = can(Permission.NC_EVALUATE) && awaitingEvaluation;
  const canClose =
    can(Permission.NC_CLOSE) &&
    nc.status !== NonconformityStatus.CLOSED &&
    nc.status !== NonconformityStatus.NOT_APPLICABLE &&
    nc.status !== NonconformityStatus.OPEN &&
    nc.status !== NonconformityStatus.UNDER_EVALUATION;

  /*
   * §A.4 — declarar reincidência é ato de quem ABRE ou AVALIA a NC, e não faz
   * sentido depois do encerramento: a NC encerrada é registro fechado, e o
   * servidor recusaria alterá-la de qualquer forma. Permissão de criação basta
   * porque o vínculo não muda estado nem criticidade — é rastreabilidade.
   */
  const canLinkRecurrence =
    (can(Permission.NC_CREATE) || can(Permission.NC_EVALUATE)) &&
    nc.status !== NonconformityStatus.CLOSED;

  const signedAct: SignableAct =
    pendingAct?.kind === 'close'
      ? 'NC_CLOSE'
      : pendingAct?.kind === 'evaluate' && !pendingAct.proceeds
        ? 'NC_NOT_APPLICABLE'
        : 'NC_EVALUATE';

  const actions = capaQuery.data?.actions ?? [];

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Não-conformidades', to: '/nao-conformidades' },
          { label: nc.code },
        ]}
        title={`${nc.code} — ${nc.title}`}
        description={`Aberta por ${nc.reporterName ?? 'usuário não identificado'} em ${formatDateOnly(nc.detectedOn)}.`}
        actions={
          <>
            {canEvaluate ? (
              <Button
                variant="primary"
                leadingIcon={<IconClipboardCheck size={15} />}
                onClick={() => setEvaluationOpen(true)}
              >
                Avaliar procedência
              </Button>
            ) : null}
            {canClose ? (
              <Button
                variant={nc.closureBlockers.length > 0 ? 'secondary' : 'primary'}
                leadingIcon={<IconCheckCircle size={15} />}
                onClick={() => setClosureOpen(true)}
              >
                Encerrar não-conformidade
              </Button>
            ) : null}
            {/*
              PDF CONSOLIDADO — §E.4. O arquivo é montado no SERVIDOR, com
              manifesto de assinaturas (§11.50(b)) e evento `EXPORT` na trilha.
              Imprimir pelo navegador não produz nenhuma das duas coisas.
            */}
            <Button
              variant="ghost"
              leadingIcon={<IconFile size={15} />}
              loading={exportPdf.isPending}
              onClick={() => exportPdf.mutate()}
            >
              Exportar PDF
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <NonconformityStatusBadge status={nc.status} />
          <Badge tone="outline" srPrefix="Natureza:">
            {NONCONFORMITY_TYPE_LABEL_PT_BR[nc.type]}
          </Badge>
          <Badge tone="outline" srPrefix="Origem:">
            {NONCONFORMITY_SOURCE_LABEL_PT_BR[nc.source]}
          </Badge>
          <CriticalityBadge
            variant="chip"
            score={nc.managerCriticalityScore ?? nc.criticalityScore}
            level={nc.managerCriticalityLevel ?? nc.criticalityLevel}
            origin={nc.managerCriticalityScore === null ? 'reporter' : 'manager'}
          />
          {nc.recurrenceCount > 0 ? (
            <Badge tone="warning" icon={<IconAlertTriangle size={12} />}>
              Reincidente de {nc.recurrenceCount}{' '}
              {nc.recurrenceCount === 1 ? 'registro' : 'registros'}
            </Badge>
          ) : null}
        </div>
      </PageHeader>

      <PageBody className="flex flex-col gap-6">
        <LifecycleTimeline nc={nc} timeZone={timeZone} />

        {/*
          O AVISO QUE O LEGADO NÃO DAVA. A NC continua aberta porque falta
          tratamento — e a lista diz exatamente o quê, com link para a ação.
        */}
        {nc.closureBlockers.length > 0 && nc.status !== NonconformityStatus.CLOSED ? (
          <ClosureBlockers blockers={nc.closureBlockers} actions={actions} />
        ) : null}

        <Tabs
          ariaLabel="Seções da não-conformidade"
          value={tab}
          onValueChange={setTab}
          tabs={[
            { value: 'dados', label: 'Dados', content: <DataTab nc={nc} /> },
            {
              value: 'avaliacao',
              label: 'Avaliação',
              count: nc.evaluations.length,
              content: <EvaluationTab nc={nc} timeZone={timeZone} />,
            },
            {
              value: 'causas',
              label: 'Análise de causa',
              count: capaQuery.data
                ? capaQuery.data.ishikawa.reduce((sum, group) => sum + group.causes.length, 0)
                : undefined,
              content: (
                <RootCausesTab
                  capaCaseId={nc.capaCaseId}
                  groups={capaQuery.data?.ishikawa ?? []}
                  loading={capaQuery.isPending && Boolean(nc.capaCaseId)}
                  // Sem análise de causa, o único caminho adiante é a avaliação
                  // de procedência — e só oferecemos o atalho a quem pode
                  // assiná-la, para o estado vazio não convidar a um beco.
                  {...(canEvaluate ? { onEvaluate: () => setEvaluationOpen(true) } : {})}
                />
              ),
            },
            {
              value: 'plano',
              label: 'Solicitações',
              count: actions.length,
              content: (
                <ActionPlanTab
                  actions={actions}
                  loading={capaQuery.isPending && Boolean(nc.capaCaseId)}
                  hasCase={Boolean(nc.capaCaseId)}
                />
              ),
            },
            {
              value: 'reunioes',
              label: 'Reuniões',
              count: nc.meetingIds.length,
              content: <MeetingsTab nonconformityId={nc.id} meetingIds={nc.meetingIds} />,
            },
            {
              value: 'reincidencia',
              label: 'Reincidência',
              count: nc.recurrences.length,
              content: <RecurrenceTab nc={nc} canEdit={canLinkRecurrence} />,
            },
            {
              value: 'evidencias',
              label: 'Evidências',
              count: nc.evidences.length,
              content: <EvidencesTab evidences={nc.evidences} timeZone={timeZone} />,
            },
            {
              value: 'trilha',
              label: 'Trilha',
              content: <AuditTab nonconformityId={nc.id} />,
            },
          ]}
        />

        <SignatureManifest
          entries={[...nc.signatures]}
          recordLabelFallback={`${nc.code} — ${nc.title}`}
        />

        <PrintableRecord
          title={`${nc.code} — ${nc.title}`}
          subtitle={`${NONCONFORMITY_TYPE_LABEL_PT_BR[nc.type]} · ${NONCONFORMITY_SOURCE_LABEL_PT_BR[nc.source]}`}
          metadata={[
            { label: 'Situação', value: NONCONFORMITY_STATUS_LABEL_PT_BR[nc.status] },
            { label: 'Data do fato', value: formatDateOnly(nc.detectedOn) },
            {
              label: 'Criticidade do emissor',
              value: `${nc.criticalityScore} — ${CRITICALITY_LABEL_PT_BR[nc.criticalityLevel]}`,
            },
            {
              label: 'Criticidade do gestor',
              value:
                nc.managerCriticalityScore === null || nc.managerCriticalityLevel === null
                  ? 'Ainda não avaliada'
                  : `${nc.managerCriticalityScore} — ${CRITICALITY_LABEL_PT_BR[nc.managerCriticalityLevel]}`,
            },
            { label: 'Responsável', value: nc.assigneeName ?? '—' },
            { label: 'Conclusão', value: nc.closingStatement ?? 'Não encerrada' },
            { label: 'Impressão em', value: formatRegulatedDateTime(new Date(), timeZone) },
          ]}
          entries={[...nc.signatures]}
        />
      </PageBody>

      {/* ── Avaliação do gestor ─────────────────────────────────────────── */}
      <EvaluationDialog
        open={evaluationOpen}
        onOpenChange={setEvaluationOpen}
        nc={nc}
        onConfirm={(act) => {
          setEvaluationOpen(false);
          setPendingAct(act);
        }}
      />

      {/* ── Encerramento ────────────────────────────────────────────────── */}
      <NcClosureDialog
        open={closureOpen}
        onOpenChange={setClosureOpen}
        blockers={nc.closureBlockers}
        actions={actions}
        onConfirm={(closingStatement) => {
          setClosureOpen(false);
          setPendingAct({ kind: 'close', closingStatement });
        }}
      />

      {pendingAct ? (
        <SignatureDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingAct(null);
          }}
          act={signedAct}
          recordId={signedActRecordId(signedAct, {
            documentVersionId: null,
            acknowledgementId: null,
            nonconformityId: nc.id,
          }) ?? ''}
          recordLabel={`${nc.code} — ${nc.title}`}
          recordDetails={[
            { label: 'Situação atual', value: NONCONFORMITY_STATUS_LABEL_PT_BR[nc.status] },
            { label: 'Natureza', value: NONCONFORMITY_TYPE_LABEL_PT_BR[nc.type] },
            { label: 'Data do fato', value: formatDateOnly(nc.detectedOn) },
            ...(pendingAct.kind === 'evaluate'
              ? [
                  {
                    label: 'Decisão',
                    value: pendingAct.proceeds ? 'Procedente' : 'Improcedente',
                  },
                  ...(pendingAct.gut
                    ? [
                        {
                          label: 'Criticidade reavaliada',
                          value: String(
                            pendingAct.gut.severity * pendingAct.gut.urgency * pendingAct.gut.trend,
                          ),
                        },
                      ]
                    : []),
                ]
              : [{ label: 'Conclusão do tratamento', value: pendingAct.closingStatement }]),
            { label: 'Impressão digital do registro', value: truncateHash(nc.recordHash) },
          ]}
          onSign={async (command) => {
            if (pendingAct.kind === 'evaluate') {
              await evaluate.mutateAsync({ command, act: pendingAct });
              return;
            }
            await close.mutateAsync({ command, act: pendingAct });
          }}
          onSigned={() => {
            // O aviso repete o nome do ato que o usuário acionou — "Encerrar
            // não-conformidade" volta como "Não-conformidade encerrada". A
            // assinatura é o meio, não o que aconteceu, e por isso desce para
            // a descrição.
            const act = pendingAct;
            setPendingAct(null);
            void invalidate();
            if (act.kind === 'close') {
              toast({
                tone: 'success',
                title: 'Não-conformidade encerrada',
                description:
                  'A conclusão do tratamento foi gravada junto com a sua assinatura eletrônica, na mesma transação.',
              });
              return;
            }
            toast({
              tone: 'success',
              title: act.proceeds ? 'Avaliação registrada' : 'Registro considerado improcedente',
              description: act.proceeds
                ? 'A não-conformidade passou a Procedente e o tratamento começa. A avaliação anterior, se houver, permanece registrada e marcada como substituída.'
                : 'O registro foi encerrado sem plano de ação. A justificativa e a sua assinatura eletrônica ficam na trilha.',
            });
          }}
        />
      ) : null}

      {/* Erros de mutação fora do diálogo não existem: o `<SignatureDialog>`
          traduz e exibe o `ErrorCode` dentro do próprio fluxo. Este bloco cobre
          o caso raro de a mutação falhar sem passar por ele. */}
      {evaluate.isError && !pendingAct ? (
        <PageBody>
          <FormError title="A avaliação não foi concluída">
            {toUserMessage(evaluate.error)}
          </FormError>
        </PageBody>
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Linha do tempo do ciclo de vida
// ─────────────────────────────────────────────────────────────────────────────

interface TimelineStep {
  readonly key: string;
  readonly label: string;
  readonly state: 'done' | 'current' | 'pending' | 'skipped';
  readonly detail: string;
}

/**
 * O ciclo de vida como LINHA DO TEMPO.
 *
 * Cada etapa carrega estado por TEXTO ("Concluída", "Etapa atual", "Pendente")
 * além do preenchimento visual — WCAG 1.4.1. O `aria-current="step"` marca onde
 * o registro está, o que dá ao leitor de tela a mesma resposta que a bolinha
 * cheia dá a quem enxerga.
 */
function LifecycleTimeline({
  nc,
  timeZone,
}: {
  readonly nc: NonconformityDetail;
  readonly timeZone: string;
}) {
  const steps = useMemo<readonly TimelineStep[]>(() => {
    const order: readonly NonconformityStatus[] = [
      NonconformityStatus.OPEN,
      NonconformityStatus.UNDER_EVALUATION,
      NonconformityStatus.ACCEPTED,
      NonconformityStatus.IN_TREATMENT,
      NonconformityStatus.PENDING_EFFECTIVENESS,
      NonconformityStatus.CLOSED,
    ];

    // "Não procede" é ramo terminal: a NC sai da trilha principal na avaliação.
    if (nc.status === NonconformityStatus.NOT_APPLICABLE) {
      const evaluation = nc.evaluations.find((item) => item.effective);
      return [
        {
          key: 'open',
          label: 'Aberta',
          state: 'done',
          detail: `Registrada em ${formatDateOnly(nc.detectedOn)}`,
        },
        {
          key: 'not-applicable',
          label: 'Improcedente',
          state: 'current',
          detail: evaluation
            ? `Avaliada por ${evaluation.evaluatedByName ?? 'gestor'} em ${formatRegulatedDateTime(evaluation.evaluatedAt, timeZone)}`
            : 'Avaliação assinada registrada',
        },
        {
          key: 'skipped',
          label: 'Tratamento',
          state: 'skipped',
          detail: 'Não se aplica: o registro foi considerado improcedente.',
        },
      ];
    }

    const currentIndex = order.indexOf(nc.status);
    return order.map((status, index) => ({
      key: status,
      label: NONCONFORMITY_STATUS_LABEL_PT_BR[status],
      state:
        index < currentIndex ? 'done' : index === currentIndex ? 'current' : ('pending' as const),
      detail: describeStep(status, nc, timeZone),
    }));
  }, [nc, timeZone]);

  return (
    <Card>
      <CardHeader
        headingLevel={2}
        title="Ciclo de vida"
        description="A não-conformidade só chega a Encerrada com todo o plano de ação concluído e a eficácia aprovada."
      />
      <ol className="mt-4 flex flex-col gap-0 sm:flex-row sm:items-start">
        {steps.map((step, index) => (
          <li
            key={step.key}
            aria-current={step.state === 'current' ? 'step' : undefined}
            className="flex min-w-0 flex-1 gap-3 sm:flex-col sm:gap-2"
          >
            <div className="flex flex-col items-center sm:w-full sm:flex-row">
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-2xs font-bold',
                  step.state === 'done' && 'border-success bg-success text-success-contrast',
                  step.state === 'current' && 'border-brand bg-brand text-brand-contrast',
                  step.state === 'pending' && 'border-border bg-surface text-content-subtle',
                  step.state === 'skipped' && 'border-border bg-surface-sunken text-content-subtle',
                )}
              >
                {step.state === 'done' ? '✓' : step.state === 'skipped' ? '–' : index + 1}
              </span>
              {index < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'my-1 w-0.5 flex-1 sm:my-0 sm:ml-2 sm:h-0.5 sm:w-full',
                    step.state === 'done' ? 'bg-success' : 'bg-border',
                  )}
                />
              ) : null}
            </div>
            <div className="min-w-0 pb-4 sm:pb-0 sm:pr-4">
              <p className="text-sm font-medium text-content">
                {step.label}
                <span className="qc-sr-only">
                  {' — '}
                  {step.state === 'done'
                    ? 'etapa concluída'
                    : step.state === 'current'
                      ? 'etapa atual'
                      : step.state === 'skipped'
                        ? 'etapa não aplicável'
                        : 'etapa pendente'}
                </span>
              </p>
              <p className="mt-0.5 text-xs text-content-muted">{step.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function describeStep(
  status: NonconformityStatus,
  nc: NonconformityDetail,
  timeZone: string,
): string {
  switch (status) {
    case NonconformityStatus.OPEN:
      return `Registrada em ${formatDateOnly(nc.detectedOn)} por ${nc.reporterName ?? 'usuário não identificado'}.`;
    case NonconformityStatus.UNDER_EVALUATION:
      return 'Aguardando a avaliação de procedência do gestor, que é ato assinado.';
    case NonconformityStatus.ACCEPTED: {
      const evaluation = nc.evaluations.find((item) => item.effective && item.proceeds);
      return evaluation
        ? `Considerada procedente em ${formatRegulatedDateTime(evaluation.evaluatedAt, timeZone)}.`
        : 'Procedência confirmada: começa a análise de causa.';
    }
    case NonconformityStatus.IN_TREATMENT:
      return 'Causas-raiz determinadas e plano de ação 5W2H em execução.';
    case NonconformityStatus.PENDING_EFFECTIVENESS:
      return 'Ações concluídas, aguardando a verificação de eficácia pelo solicitante.';
    case NonconformityStatus.CLOSED:
      return nc.closedAt
        ? `Encerrada em ${formatRegulatedDateTime(nc.closedAt, timeZone)}.`
        : 'Encerramento assinado, com todo o plano de ação concluído e eficaz.';
    default:
      return '';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Impedimentos de encerramento
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POR QUE O BOTÃO DE ENCERRAR NÃO FUNCIONA AGORA.
 *
 * `closureBlockers` vem do servidor, que é quem aplica `assertCapaIsComplete`
 * dentro da transação do ato. Repetir a lista aqui não substitui a invariante —
 * substitui o SILÊNCIO: no legado a regra existia num endpoint sem chamador e a
 * NC era encerrada sem nada disso ser verificado.
 *
 * Cada ação pendente aparece com LINK, porque "existem 3 ações não concluídas"
 * sem dizer quais é uma mensagem que não resolve nada.
 */
function ClosureBlockers({
  blockers,
  actions,
}: {
  readonly blockers: readonly string[];
  readonly actions: readonly CapaActionView[];
}) {
  const pending = actions.filter(
    (action) =>
      action.status !== CapaActionStatus.EFFECTIVE &&
      action.status !== CapaActionStatus.CANCELLED,
  );
  const rejected = actions.filter((action) => action.effectivenessApproved === false);

  return (
    <Card className="border-l-4 border-l-warning">
      <div className="flex items-start gap-3">
        <IconAlertTriangle
          size={20}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-warning-content"
        />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-content">
            O encerramento está bloqueado
          </h2>
          <p className="mt-1 text-sm text-content-muted">
            ISO 13485 §8.5.2 exige verificar que a ação corretiva foi eficaz antes de dar o assunto
            por resolvido. Enquanto os pontos abaixo existirem, o servidor recusa o encerramento —
            mesmo que o botão seja acionado.
          </p>

          <ul className="mt-3 flex flex-col gap-1.5">
            {blockers.map((blocker) => (
              <li key={blocker} className="flex items-start gap-2 text-sm text-content">
                <IconAlertOctagon
                  size={14}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0 text-danger-content"
                />
                <span>{blocker}</span>
              </li>
            ))}
          </ul>

          {pending.length > 0 || rejected.length > 0 ? (
            <div className="mt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
                Ações que precisam de atenção
              </h3>
              <ul className="mt-2 flex flex-col gap-1.5">
                {[...rejected, ...pending.filter((action) => !rejected.includes(action))].map(
                  (action) => (
                    <li key={action.id}>
                      <Link
                        to="/acoes-corretivas/$actionId"
                        params={{ actionId: action.id }}
                        className={cn(
                          'flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken',
                          focusRing,
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate font-medium text-brand">
                          {action.title}
                        </span>
                        <CapaActionStatusBadge status={action.status} />
                        <CapaDueBadge
                          variant="chip"
                          state={action.dueState}
                          status={action.status}
                          dueDate={action.plan.when}
                        />
                        {action.effectivenessApproved === false ? (
                          <Badge tone="danger" size="sm">
                            Eficácia reprovada
                          </Badge>
                        ) : null}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Diálogo de avaliação
// ─────────────────────────────────────────────────────────────────────────────

function EvaluationDialog({
  open,
  onOpenChange,
  nc,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly nc: NonconformityDetail;
  readonly onConfirm: (act: PendingAct) => void;
}) {
  const [proceeds, setProceeds] = useState<'yes' | 'no'>('yes');
  const [gut, setGut] = useState<Partial<GutScoreInput>>({
    severity: nc.gut.severity,
    urgency: nc.gut.urgency,
    trend: nc.gut.trend,
  });

  const gutComplete =
    typeof gut.severity === 'number' &&
    typeof gut.urgency === 'number' &&
    typeof gut.trend === 'number';

  const blocked = proceeds === 'yes' && !gutComplete;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Avaliação de procedência"
      description="Decida se o registro procede e, sendo procedente, refaça a matriz GUT. A justificativa é digitada no passo seguinte, junto da assinatura eletrônica — ela é obrigatória nos dois caminhos."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant={proceeds === 'yes' ? 'primary' : 'danger-outline'}
            disabled={blocked}
            onClick={() =>
              onConfirm({
                kind: 'evaluate',
                proceeds: proceeds === 'yes',
                ...(proceeds === 'yes' && gutComplete
                  ? {
                      gut: {
                        severity: gut.severity as number,
                        urgency: gut.urgency as number,
                        trend: gut.trend as number,
                      },
                    }
                  : {}),
              })
            }
          >
            Continuar para a assinatura
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <RadioGroup<'yes' | 'no'>
          legend="A não-conformidade procede?"
          value={proceeds}
          onValueChange={setProceeds}
          options={[
            {
              value: 'yes',
              label: 'Procede',
              description:
                'O fato é uma não-conformidade real. O registro passa a Procedente e o tratamento começa: análise de causa, plano de ação e verificação de eficácia. Não é o encerramento.',
            },
            {
              value: 'no',
              label: 'Não procede',
              description:
                'O fato não configura não-conformidade. O registro é encerrado como Improcedente, sem plano de ação, e a justificativa fica na trilha.',
            },
          ]}
        />

        {proceeds === 'yes' ? (
          <>
            <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
              <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span>
                A sua reavaliação vai para uma coluna separada e{' '}
                <strong className="font-semibold">não substitui</strong> a avaliação do emissor
                (
                {nc.criticalityScore} — {CRITICALITY_LABEL_PT_BR[nc.criticalityLevel]}). As duas
                ficam registradas e comparáveis.
              </span>
            </p>
            <GutMatrix
              value={gut}
              onChange={setGut}
              legend="Reavaliação da matriz GUT"
              description="Refaça os três fatores com a informação que você tem como gestor. É este score que prioriza a fila de tratamento."
            />
          </>
        ) : (
          <p className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content">
            <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Registrar “não procede” <strong className="font-semibold">encerra</strong> a
              não-conformidade sem plano de ação. A decisão é irreversível: a máquina de estados não
              tem caminho de volta para Aberta.
            </span>
          </p>
        )}

        {blocked ? (
          <p role="alert" className="text-sm font-medium text-danger-content">
            Complete os três fatores da matriz GUT para prosseguir. Sem a reavaliação, “procede” não
            diz o quanto o problema importa.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Abas
// ─────────────────────────────────────────────────────────────────────────────

function DataTab({ nc }: { readonly nc: NonconformityDetail }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader headingLevel={3} title="Identificação" />
        <dl className="mt-3 grid grid-cols-[11rem_1fr] gap-x-4 gap-y-2 text-sm">
          <DefinitionRow label="Número" value={nc.code} />
          <DefinitionRow label="Título" value={nc.title} />
          <DefinitionRow label="Origem" value={NONCONFORMITY_SOURCE_LABEL_PT_BR[nc.source]} />
          <DefinitionRow label="Natureza" value={NONCONFORMITY_TYPE_LABEL_PT_BR[nc.type]} />
          <DefinitionRow label="Data do fato" value={formatDateOnly(nc.detectedOn)} />
          <DefinitionRow label="Emissor" value={nc.reporterName ?? '—'} />
          <DefinitionRow label="Responsável" value={nc.assigneeName ?? '—'} />
        </dl>
      </Card>

      <Card>
        <CardHeader
          headingLevel={3}
          title="Criticidade GUT"
          description="Duas avaliações independentes, em colunas separadas: a do emissor, feita na abertura, e a do gestor, feita na avaliação de procedência."
        />
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <CriticalityBadge
            variant="block"
            score={nc.criticalityScore}
            level={nc.criticalityLevel}
            origin="reporter"
          />
          {nc.managerCriticalityScore !== null && nc.managerCriticalityLevel !== null ? (
            <CriticalityBadge
              variant="block"
              score={nc.managerCriticalityScore}
              level={nc.managerCriticalityLevel}
              origin="manager"
            />
          ) : (
            <div className="rounded-md border border-border bg-surface-sunken px-3 py-2">
              <p className="text-sm font-semibold text-content">Reavaliação do gestor</p>
              <p className="mt-0.5 text-xs text-content-muted">Ainda não realizada.</p>
            </div>
          )}
        </div>

        <dl className="mt-4 grid grid-cols-[11rem_1fr] gap-x-4 gap-y-2 text-sm">
          <DefinitionRow label="Gravidade (emissor)" value={String(nc.gut.severity)} />
          <DefinitionRow label="Urgência (emissor)" value={String(nc.gut.urgency)} />
          <DefinitionRow label="Tendência (emissor)" value={String(nc.gut.trend)} />
          {nc.managerGut ? (
            <>
              <DefinitionRow label="Gravidade (gestor)" value={String(nc.managerGut.severity)} />
              <DefinitionRow label="Urgência (gestor)" value={String(nc.managerGut.urgency)} />
              <DefinitionRow label="Tendência (gestor)" value={String(nc.managerGut.trend)} />
            </>
          ) : null}
        </dl>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader
          headingLevel={3}
          title="Evidência do fato"
          description="O fato observado, como foi registrado na abertura."
        />
        <p className="mt-3 whitespace-pre-line text-sm text-content">{nc.evidence}</p>
      </Card>

      {nc.closingStatement ? (
        <Card className="lg:col-span-2 border-l-4 border-l-success">
          <CardHeader headingLevel={3} title="Conclusão do tratamento" />
          <p className="mt-3 whitespace-pre-line text-sm text-content">{nc.closingStatement}</p>
        </Card>
      ) : null}
    </div>
  );
}

function EvaluationTab({
  nc,
  timeZone,
}: {
  readonly nc: NonconformityDetail;
  readonly timeZone: string;
}) {
  if (nc.evaluations.length === 0) {
    return (
      <EmptyState
        icon={<IconClipboardCheck size={22} />}
        title="Ainda sem avaliação de procedência"
        description="O gestor precisa decidir se o registro procede. É ato assinado eletronicamente e exige justificativa nos dois caminhos."
      />
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {nc.evaluations.map((evaluation) => (
        <li key={evaluation.id}>
          <Card className={cn(evaluation.effective && 'border-l-4 border-l-brand')}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-content">
                  {evaluation.proceeds ? 'Procedente' : 'Improcedente'}
                  {evaluation.effective ? <Badge tone="brand">Vigente</Badge> : (
                    <Badge tone="neutral">Substituída</Badge>
                  )}
                </h3>
                <p className="mt-1 text-sm text-content-muted">
                  Avaliada por{' '}
                  <span className="font-medium text-content">
                    {evaluation.evaluatedByName ?? 'gestor não identificado'}
                  </span>{' '}
                  em {formatRegulatedDateTime(evaluation.evaluatedAt, timeZone)}
                </p>
              </div>
              {evaluation.criticalityScore !== null && evaluation.criticalityLevel !== null ? (
                <CriticalityBadge
                  variant="chip"
                  score={evaluation.criticalityScore}
                  level={evaluation.criticalityLevel}
                  origin="manager"
                />
              ) : null}
            </div>

            <div className="mt-3 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
                Justificativa
              </p>
              <p className="mt-1 whitespace-pre-line text-sm text-content">
                {evaluation.justification}
              </p>
            </div>

            <p className="mt-2 text-xs text-content-subtle">
              Assinatura: <span className="font-mono">{truncateHash(evaluation.signatureId, 8, 6)}</span>
            </p>
          </Card>
        </li>
      ))}
    </ol>
  );
}

function RootCausesTab({
  capaCaseId,
  groups,
  loading,
  onEvaluate,
}: {
  readonly capaCaseId: string | null;
  readonly groups: readonly IshikawaGroup[];
  readonly loading: boolean;
  readonly onEvaluate?: () => void;
}) {
  if (!capaCaseId) {
    return (
      <EmptyState
        icon={<IconClipboardCheck size={22} />}
        title="A análise de causa começa depois da avaliação de procedência"
        description="O gestor precisa considerar a não-conformidade procedente. Até lá não há Ishikawa nem plano de ação a registrar."
        {...(onEvaluate
          ? {
              action: (
                <Button
                  variant="primary"
                  leadingIcon={<IconClipboardCheck size={15} />}
                  onClick={onEvaluate}
                >
                  Avaliar procedência
                </Button>
              ),
            }
          : {})}
      />
    );
  }

  if (loading) {
    return (
      <LoadingRegion loading label="Carregando a análise de causa…">
        <Skeleton className="h-56 w-full" />
      </LoadingRegion>
    );
  }

  return (
    <>
      <IshikawaBoard groups={groups} readOnly />
      <p className="mt-3 flex items-start gap-2 text-sm text-content-muted">
        <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
        <span>
          A análise de causa é conduzida dentro da reunião SACAP, onde as causas são registradas,
          priorizadas e promovidas a ações. Esta aba mostra o resultado consolidado.
        </span>
      </p>
    </>
  );
}

function ActionPlanTab({
  actions,
  loading,
  hasCase,
}: {
  readonly actions: readonly CapaActionView[];
  readonly loading: boolean;
  readonly hasCase: boolean;
}) {
  const columns = useMemo<Array<TableColumn<CapaActionView>>>(
    () => [
      {
        id: 'title',
        header: 'Ação',
        width: '32%',
        cell: (row) => (
          <div className="min-w-0">
            <Link
              to="/acoes-corretivas/$actionId"
              params={{ actionId: row.id }}
              className={cn('block truncate font-medium text-brand hover:underline', focusRing)}
            >
              {row.title}
            </Link>
            <p className="mt-0.5 truncate text-xs text-content-subtle">
              Causa: {row.rootCauseDescription}
            </p>
          </div>
        ),
      },
      {
        id: 'assignee',
        header: 'Responsável',
        width: '18%',
        cell: (row) => (
          <span className="text-sm text-content-muted">{row.assigneeName ?? '—'}</span>
        ),
      },
      {
        id: 'due',
        header: 'Prazo',
        width: '16%',
        cell: (row) => (
          <CapaDueBadge state={row.dueState} status={row.status} dueDate={row.plan.when} />
        ),
      },
      {
        id: 'status',
        header: 'Situação',
        width: '20%',
        cell: (row) => <CapaActionStatusBadge status={row.status} />,
      },
      {
        id: 'kind',
        header: 'Natureza',
        width: '14%',
        cell: (row) => (
          <Badge tone="outline" size="sm">
            {row.preventive ? 'Preventiva' : 'Corretiva'}
          </Badge>
        ),
      },
    ],
    [],
  );

  if (!hasCase) {
    return (
      <EmptyState
        icon={<IconClipboardCheck size={22} />}
        title="Sem solicitações"
        /*
         * O texto era categórico — "o plano nasce SEMPRE a partir de uma
         * causa-raiz" — e deixou de ser verdade no sistema: existe solicitação
         * avulsa. Continua verdade AQUI, dentro da não-conformidade, e é isso
         * que a frase agora diz, sem generalizar para o resto do produto.
         */
        description="Dentro de uma não-conformidade, a solicitação nasce depois da avaliação de procedência e a partir de uma causa-raiz — ação sem causa trata sintoma (ISO 13485 §8.5.2). Pedidos que não tratam não-conformidade são abertos em Solicitações."
      />
    );
  }

  return (
    <Table<CapaActionView>
      caption="Solicitações desta não-conformidade, com o plano 5W2H de cada uma: responsável, prazo, situação e natureza."
      columns={columns}
      rows={[...actions]}
      rowKey={(row) => row.id}
      loading={loading}
      density="comfortable"
      empty={
        <EmptyState
          variant="compact"
          icon={<IconClipboardCheck size={20} />}
          title="Nenhuma ação corretiva registrada"
          description="Registre as causas-raiz na reunião SACAP e promova cada uma a ação corretiva."
        />
      }
    />
  );
}

function MeetingsTab({
  nonconformityId,
  meetingIds,
}: {
  readonly nonconformityId: string;
  readonly meetingIds: readonly string[];
}) {
  const { can } = useAuth();

  if (meetingIds.length === 0) {
    /*
     * O convite tem que ser aceitável no próprio botão: mandar para a lista
     * filtrada, que acabou de ser declarada vazia, devolve o usuário ao mesmo
     * lugar. O formulário de reunião já lê `nonconformityId` da URL e abre com
     * a não-conformidade vinculada.
     */
    const canSchedule = can(Permission.MEETING_MANAGE);
    return (
      <EmptyState
        icon={<IconCalendar size={22} />}
        title="Nenhuma reunião vinculada"
        description={
          canSchedule
            ? 'A análise de causa acontece na reunião SACAP. Agende uma reunião já vinculada a esta não-conformidade para registrar o Ishikawa e o plano de ação.'
            : 'A análise de causa acontece na reunião SACAP. Peça ao gestor da qualidade uma reunião vinculada a esta não-conformidade.'
        }
        {...(canSchedule
          ? {
              action: (
                <Button asChild variant="primary" leadingIcon={<IconCalendar size={15} />}>
                  <Link to="/reunioes/nova" search={{ nonconformityId }}>
                    Agendar reunião para esta não-conformidade
                  </Link>
                </Button>
              ),
            }
          : {})}
      />
    );
  }

  return (
    <Card>
      <CardHeader
        headingLevel={3}
        title={`Reuniões vinculadas (${meetingIds.length})`}
        description="Toda causa-raiz e todo plano de ação desta não-conformidade nascem numa destas reuniões."
      />
      <div className="mt-4">
        <Button asChild variant="primary" leadingIcon={<IconCalendar size={15} />}>
          <Link to="/reunioes" search={{ nonconformityId }}>
            Abrir a lista filtrada por esta não-conformidade
          </Link>
        </Button>
      </div>
    </Card>
  );
}

/**
 * ABA REINCIDÊNCIA — §A.4.
 *
 * Deixou de ser somente leitura. A auditoria da Onda 2 (A27–A31) registrou que
 * `POST /nonconformities/:id/recurrences` existia no gateway, no contrato e no
 * serviço, e que o ÚNICO chamador era o teste de contrato — a aba, portanto,
 * exibiria "Sem reincidência registrada" para sempre e a taxa de reincidência
 * dos indicadores seria permanentemente 0%.
 *
 * O que NÃO mudou é a regra: o vínculo não altera criticidade, não muda
 * situação, não força SACAP e não notifica. O legado estava certo nisso, e
 * transformá-lo em gatilho automático embutiria política de SGQ no produto.
 */
function RecurrenceTab({
  nc,
  canEdit,
}: {
  readonly nc: NonconformityDetail;
  readonly canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [originIds, setOriginIds] = useState<string[]>([]);
  const [note, setNote] = useState('');

  const link = useMutation({
    mutationFn: () =>
      nonconformitiesApi.addRecurrences(nc.id, {
        originNonconformityIds: originIds,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      setOpen(false);
      setOriginIds([]);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.nonconformities.all() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sgqIndicators.all() });
      toast({
        tone: 'success',
        title: 'Reincidência registrada',
        description:
          'O vínculo entra na trilha e alimenta a taxa de reincidência. Nada mais muda: criticidade, situação e plano de ação seguem como estavam.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível registrar a reincidência',
        description: toUserMessage(error),
      }),
  });

  const alreadyLinked = nc.recurrences.map((row) => row.originNonconformityId);

  const dialog = (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (link.isPending) return;
        setOpen(next);
      }}
      title="Declarar reincidência"
      description="Aponte as não-conformidades anteriores que já haviam registrado o mesmo problema. O vínculo é informativo e rastreável — não altera criticidade nem situação."
      dismissible={!link.isPending}
      footer={
        <>
          <Button variant="secondary" disabled={link.isPending} onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={link.isPending}
            disabled={originIds.length === 0}
            onClick={() => link.mutate()}
          >
            Registrar vínculo
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <NonconformityPicker
          selectedIds={originIds}
          onSelectedIdsChange={(ids) =>
            // A auto-relação é recusada pelo servidor (`ncRecurrenceSelf`); a
            // origem já vinculada seria ignorada. Filtrar aqui evita oferecer
            // ao usuário uma escolha que não vai a lugar nenhum.
            setOriginIds(ids.filter((id) => !alreadyLinked.includes(id)))
          }
          excludeId={nc.id}
          label="Não-conformidades de origem"
          description="Busque por número ou título, entre as não-conformidades da organização."
          disabled={link.isPending}
        />
        <Field
          label="Observação"
          description="Opcional. Fica registrada junto ao vínculo e aparece na trilha."
        >
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={2000}
            showCounter
            disabled={link.isPending}
          />
        </Field>
      </div>
    </Dialog>
  );

  if (nc.recurrences.length === 0) {
    return (
      <>
        <EmptyState
          icon={<IconHistory size={22} />}
          title="Sem reincidência registrada"
          description="Esta não-conformidade não foi declarada como repetição de nenhum registro anterior. O vínculo é informativo e alimenta o indicador de taxa de reincidência."
          {...(canEdit
            ? {
                action: (
                  <Button variant="primary" onClick={() => setOpen(true)}>
                    Declarar reincidência
                  </Button>
                ),
              }
            : {})}
        />
        {dialog}
      </>
    );
  }

  return (
    <>
      <Card padding="none">
        <div className="flex flex-wrap items-start justify-between gap-3 p-4">
          <CardHeader
            headingLevel={3}
            title={`Registros de origem (${nc.recurrences.length})`}
            description="Não-conformidades anteriores que já haviam registrado o mesmo problema. O vínculo não altera criticidade, não muda situação e não força reunião — é rastreabilidade."
          />
          {canEdit ? (
            <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
              Acrescentar origem
            </Button>
          ) : null}
        </div>
        <ul className="border-t border-border">
          {nc.recurrences.map((recurrence) => (
            <li key={recurrence.id} className="border-b border-border-subtle last:border-b-0">
              <Link
                to="/nao-conformidades/$nonconformityId"
                params={{ nonconformityId: recurrence.originNonconformityId }}
                className={cn(
                  'flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-surface-sunken',
                  focusRing,
                )}
              >
                <span className="font-mono text-sm font-medium text-brand">
                  {recurrence.originCode}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-content">
                  {recurrence.originTitle}
                </span>
                {recurrence.note ? (
                  <span className="text-xs text-content-subtle">{recurrence.note}</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      </Card>
      {dialog}
    </>
  );
}

function EvidencesTab({
  evidences,
  timeZone,
}: {
  readonly evidences: readonly NonconformityEvidence[];
  readonly timeZone: string;
}) {
  if (evidences.length === 0) {
    return (
      <EmptyState
        icon={<IconFile size={22} />}
        title="Nenhum arquivo de evidência"
        description="A não-conformidade foi aberta sem anexos. A evidência do fato, na aba Dados, continua sendo o registro textual."
      />
    );
  }

  return (
    <ul aria-label="Arquivos de evidência" className="flex flex-col gap-2">
      {evidences.map((evidence) => {
        const scan = describeScan(evidence.scanResult);
        return (
          <li
            key={evidence.id}
            className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5"
          >
            <IconFile size={18} aria-hidden="true" className="shrink-0 text-content-subtle" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-content">{evidence.fileName}</p>
              <p className="text-xs text-content-subtle qc-tabular">
                {formatBytes(evidence.sizeBytes)} · {evidence.mimeType} · anexado em{' '}
                {formatRegulatedDateTime(evidence.createdAt, timeZone)}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <Badge tone={scan.tone} size="sm" icon={scan.icon}>
                Antivírus: {scan.label}
              </Badge>
              <span
                className="font-mono text-2xs text-content-subtle"
                title={evidence.checksumSha256}
              >
                SHA-256 {truncateHash(evidence.checksumSha256, 8, 8)}
              </span>
            </div>
            {/* A consequência ocupa a linha inteira: é frase, não rótulo, e no
                text-2xs do badge ela seria conteúdo em tamanho auxiliar. */}
            {scan.consequence ? (
              <p className="w-full text-xs text-content">{scan.consequence}</p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

interface ScanPresentation {
  readonly label: string;
  readonly tone: BadgeTone;
  readonly icon: ReactNode;
  /** O que o usuário precisa fazer. `null` quando não há nada a fazer. */
  readonly consequence: string | null;
}

/**
 * A varredura em texto de tela.
 *
 * O valor que chega é o do serviço de mídia (`PENDING | CLEAN | INFECTED |
 * ERROR`) e é nome interno em inglês — justamente no momento em que o usuário
 * precisa entender por que o arquivo não abre. Cada situação sai com nome em
 * português, ícone de forma distinta (a cor não decide sozinha) e o próximo
 * passo.
 */
function describeScan(scanResult: string): ScanPresentation {
  switch (scanResult) {
    case 'CLEAN':
      return {
        label: 'limpo',
        tone: 'success',
        icon: <IconCheckCircle size={12} />,
        consequence: null,
      };
    case 'PENDING':
      return {
        label: 'em verificação',
        tone: 'warning',
        icon: <IconClock size={12} />,
        consequence: 'A verificação ainda está em andamento e o arquivo não pode ser baixado.',
      };
    case 'INFECTED':
      return {
        label: 'ameaça detectada',
        tone: 'danger',
        icon: <IconAlertOctagon size={12} />,
        consequence: 'O arquivo foi bloqueado e não pode ser baixado. Anexe outro no lugar.',
      };
    default:
      // `ERROR` e qualquer situação nova caem aqui: sem varredura concluída o
      // arquivo fica indisponível, e o caminho de saída é reanexar.
      return {
        label: 'verificação não concluída',
        tone: 'warning',
        icon: <IconAlertTriangle size={12} />,
        consequence: 'O arquivo não pode ser baixado. Anexe o arquivo de novo.',
      };
  }
}

function AuditTab({ nonconformityId }: { readonly nonconformityId: string }) {
  return (
    <Card>
      <CardHeader
        headingLevel={3}
        title="Trilha de auditoria desta não-conformidade"
        description="Todos os eventos do registro, em banco separado, sem permissão de alteração nem exclusão para ninguém."
      />
      <div className="mt-4 flex flex-wrap gap-2">
        <Button asChild variant="primary" leadingIcon={<IconHistory size={15} />}>
          <Link
            to="/auditoria"
            search={{ entityId: nonconformityId, entityType: 'Nonconformity', page: 1 }}
          >
            Abrir a trilha filtrada por esta não-conformidade
          </Link>
        </Button>
        <Button asChild variant="secondary" leadingIcon={<IconUsers size={15} />}>
          <Link to="/auditoria" search={{ entityId: nonconformityId, onlySigned: true, page: 1 }}>
            Ver apenas os atos assinados
          </Link>
        </Button>
      </div>
    </Card>
  );
}

function DefinitionRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <>
      <dt className="text-content-subtle">{label}</dt>
      <dd className="min-w-0 break-words text-content">{value}</dd>
    </>
  );
}
